import { Table, Badge, Group, ActionIcon, Tooltip, Text, Button, Box, LoadingOverlay, Image } from '@mantine/core';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSettingsSelector, selectSettingLoading } from '../../../redux/selectors/settingSelector';
import { getAllSettings } from '../../../redux/reducers/settingSlice';
import { Setting, SettingKey, SettingType } from '../../../models/setting.model';
import { IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import { selectIsAdminOrOwner } from '../../../redux/selectors/authSelector';
import AddEditSetting from './add_edit_setting/add_edit_setting';
import colors from '../../../utils/styles';

function SettingsList() {
  const dispatch = useDispatch();
  const settings = useSelector(selectSettingsSelector);
  const loading = useSelector(selectSettingLoading);
  const isAdminOrOwner = useSelector(selectIsAdminOrOwner);
  const [modalOpened, setModalOpened] = useState(false);
  const [activeSetting, setActiveSetting] = useState<Setting | null>(null);

  useEffect(() => {
    dispatch(getAllSettings());
  }, [dispatch]);

  const handleOpenModal = (setting: Setting | null = null) => {
    setActiveSetting(setting);
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setActiveSetting(null);
    setModalOpened(false);
  };

  const isColorSetting = (setting: Setting) => {
    return setting.setting_key === SettingKey.AVAILABLE_SHOW_COLOR ||
      setting.setting_key === SettingKey.ALMOST_FULL_SHOW_COLOR ||
      setting.setting_key === SettingKey.FULL_SHOW_COLOR;
  };

  const settingKeyTranslation = (setting: Setting) => {
    switch (setting.setting_key) {
      case SettingKey.AVAILABLE_SHOW_COLOR:
        return 'Available Show Color';
      case SettingKey.ALMOST_FULL_SHOW_COLOR:
        return 'Almost Full Show Color';
      case SettingKey.FULL_SHOW_COLOR:
        return 'Full Show Color';
      case SettingKey.DEFAULT_MAX_CAPACITY:
        return 'Default Max Capacity';
      case SettingKey.DEFAULT_MAX_CHILDREN_CAPACITY:
        return 'Default Max Children Capacity';
      case SettingKey.ENABLE_API_PAYMENT_CONNECTION:
        return 'Enable API Payment Connection';
      case SettingKey.ENABLE_PAYMENT_CUSTOMER_DETAILS:
        return 'Enable Payment Customer Details';
      case SettingKey.LIMITED_AVAILABILITY_THRESHOLD:
        return 'Almost Full Threshold';
      case SettingKey.SEND_EMAIL_ON_BOOKING:
        return 'Send Email on Booking';
      case SettingKey.CHILD_TICKET_PRICE:
        return 'Child Ticket Price';
      case SettingKey.STANDARD_TICKET_PRICE:
        return 'Standard Ticket Price';
      case SettingKey.VIP_TICKET_PRICE:
        return 'VIP Ticket Price';
      case SettingKey.VIP_TICKET_CHILD_PRICE:
        return 'VIP Ticket Child Price';
      case SettingKey.VIVA_PAYMENT_CONFIGURATION:
        return 'Viva Payment Configuration';
      case SettingKey.GMAIL_SMTP_CONFIGURATION:
        return 'Gmail SMTP Configuration';
      case SettingKey.VIP_IMAGE_URL:
        return 'VIP Image URL';
      case SettingKey.GOOGLE_MAPS_TYPE:
        return 'Google Maps Type';
      case SettingKey.ADDITIONAL_FEE_PERCENTAGE:
        return 'Additional Fee Percentage';
      case SettingKey.MAINTENANCE_MODE:
        return 'Maintenance Mode';
      default:
        return setting.setting_key;
    }
  };

  const getCategoryColor = (category: string | undefined) => {
    switch (category?.toLowerCase()) {
      case 'email':
        return 'blue';
      case 'payment':
        return 'green';
      case 'booking':
        return 'yellow';
      case 'system':
        return 'red';
      case 'theater':
        return 'purple';
      case 'display':
        return 'orange';
      case 'general':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getSettingDisplayValue = (setting: Setting) => {
    if (setting.setting_type === SettingType.IMAGE) return <Image src={setting.setting_value as string} height={100} alt="VIP Image" />;
    if (setting.setting_type === SettingType.JSON) return 'Custom Configuration';
    return typeof setting.setting_value === SettingType.BOOLEAN
      ? !setting.setting_value ? <span style={{ color: 'red' }}>Disabled</span> : <span style={{ color: 'green' }}>Enabled</span>
      : isColorSetting(setting) ? <div style={{ backgroundColor: setting.setting_value as string, width: '100%', height: '14px', borderRadius: '5px' }}></div> : setting.setting_value?.toString()?.toUpperCase();
  }

  const rows = useMemo(() => settings?.map((setting: Setting) => (
    <Table.Tr key={setting._id}>
      <Table.Td>
        {settingKeyTranslation(setting)}
      </Table.Td>
      <Table.Td>
        <Text lineClamp={2} fw={700}>
          {getSettingDisplayValue(setting)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text lineClamp={2}>
          {setting.description || '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        {setting.category ? (
          <Badge color={getCategoryColor(setting.category)}>
            {setting.category}
          </Badge>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        {setting.setting_updated_at ? (
          new Date(setting.setting_updated_at).toLocaleDateString()
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs"wrap='nowrap'>
          <Tooltip label="Edit Setting">
            <ActionIcon
              size='lg'
              variant="outline"
              color="blue"
              onClick={() => handleOpenModal(setting)}
              disabled={!isAdminOrOwner}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete Setting">
            <ActionIcon size='lg' variant="outline" color="red" disabled={true}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  )), [settings, isAdminOrOwner]);

  return (
    <Box>
      <Group justify="flex-end" mb="md">
        <Button
          disabled={!isAdminOrOwner}
          leftSection={<IconPlus size={16} />}
          onClick={() => handleOpenModal()}
        >
          Add Setting
        </Button>
      </Group>

      <Box style={{ position: 'relative' }}>
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "md", blur: 0.5 }}
          loaderProps={{ color: colors.appDarkBlue, type: 'bars' }}
        />
        <Table stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Setting</Table.Th>
              <Table.Th>Value</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Last Updated</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Box>

      <AddEditSetting
        opened={modalOpened}
        onClose={handleCloseModal}
        activeSetting={activeSetting}
      />
    </Box>
  );
}

export default SettingsList;